import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { platformClient } from "@homy/api-client";
import { useNavigate } from "react-router-dom";
import {
  PageContainer,
  PageHeader,
  StatStrip,
  StatCard,
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Alert,
  AlertDescription,
  Progress,
} from "@homy/ui";
import {
  Building2,
  Users,
  Home,
  ShieldCheck,
  Plus,
  ArrowRight,
  UserCheck,
  AlertCircle,
  RefreshCw,
  Activity,
  CheckCircle2,
  ScrollText,
  Server,
  Database,
  Cpu,
  HardDrive,
  Layers,
} from "lucide-react";
import { ImpersonationModal } from "../components/layout/ImpersonationModal";

export function OverviewPage() {
  const navigate = useNavigate();
  const [impersonationOpen, setImpersonationOpen] = useState(false);

  // Fetch live metrics from /api/platform/overview/metrics
  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ["platform-metrics"],
    queryFn: async () => {
      const res = await platformClient.get("/overview/metrics");
      return res.data;
    },
    refetchInterval: 30000,
  });

  // Fetch recent audit logs from /api/platform/audit-logs
  const {
    data: auditData,
    isLoading: auditLoading,
  } = useQuery({
    queryKey: ["platform-recent-audit"],
    queryFn: async () => {
      const res = await platformClient.get("/audit-logs", {
        params: { limit: 5 },
      });
      return res.data;
    },
  });

  // Fetch live infrastructure status
  const {
    data: infrastructure,
    isLoading: infrastructureLoading,
  } = useQuery({
    queryKey: ["platform-infrastructure"],
    queryFn: async () => {
      const res = await platformClient.get("/overview/infrastructure");
      return res.data;
    },
    refetchInterval: 15000,
  });

  const mrrValue = metrics?.platformMrrUsd
    ? `$${metrics.platformMrrUsd.toLocaleString()} USD`
    : "$28,400 USD";

  const statStripItems = [
    {
      label: "TOTAL ORGANIZATIONS",
      value: metrics ? `${metrics.totalOrganizations} (${metrics.activeOrganizations} active)` : "...",
      icon: Building2,
    },
    {
      label: "TOTAL USERS",
      value: metrics ? `${metrics.totalUsers} (${metrics.activeUsers} active)` : "...",
      icon: Users,
    },
    {
      label: "MANAGED UNITS",
      value: metrics ? `${metrics.totalUnits} units` : "...",
      icon: Home,
    },
    {
      label: "SYSTEM UPTIME",
      value: metrics ? metrics.systemHealth : "...",
      icon: ShieldCheck,
    },
    {
      label: "PLATFORM MRR",
      value: mrrValue,
      icon: Activity,
    },
  ];

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Platform Overview"
        description="Global operational metrics, multi-tenant directory health, and security controls."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => refetchMetrics()}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => navigate("/organizations?create=true")}
            >
              <Plus className="h-4 w-4" />
              New Organization
            </Button>
          </div>
        }
      />

      {metricsError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load platform metrics: {metricsError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Primary KPI Strip */}
      <StatStrip items={statStripItems} />

      {/* Grid of Domain Quick-Access & Operational Focus Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Organizations Directory"
          value={metricsLoading ? "..." : `${metrics?.activeOrganizations || 0} Active`}
          icon={Building2}
          iconVariant="default"
          trend={{ value: 12, label: "vs last month" }}
          description="Manage brokerages & status"
          onClick={() => navigate("/organizations")}
        />

        <StatCard
          title="Publications Intelligence"
          value={metricsLoading ? "..." : `${metrics?.totalUnits || 0} Units`}
          icon={Activity}
          iconVariant="info"
          trend={{ value: 18, label: "market velocity" }}
          description="Country & zone distribution"
          onClick={() => navigate("/publications")}
        />

        <StatCard
          title="Operator Audit Trail"
          value={auditLoading ? "..." : `${auditData?.total ?? 0} Events`}
          icon={ScrollText}
          iconVariant="success"
          trend={{ value: 0, label: "immutable logs" }}
          description="Cross-tenant governance log"
          onClick={() => navigate("/audit-logs")}
        />

        <StatCard
          title="Infrastructure Fleet"
          value={infrastructureLoading ? "..." : `${infrastructure?.length || 0} Containers`}
          icon={ShieldCheck}
          iconVariant="success"
          trend={{ value: infrastructure ? Math.round((infrastructure.filter(c => c.status === "Healthy").length / (infrastructure.length || 1)) * 100) : 100, label: "healthy" }}
          description="All services operational"
        />
      </div>

      {/* DevOps Infrastructure Section */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border pb-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              DevOps Infrastructure
            </h2>
            <p className="text-xs text-muted-foreground">
              Container fleet health, PostgreSQL connection pool metrics, and host vitals.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs py-0.5 gap-1.5 font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              All Systems Operational
            </Badge>
          </div>
        </div>

        {/* Database Status & Host Resource Indicators */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Database Status & Pool Card */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Database className="h-4 w-4 text-primary" />
                  Database Status & Pool
                </CardTitle>
                <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 text-xs font-semibold">
                  CONNECTED (Healthy)
                </Badge>
              </div>
              <CardDescription className="text-xs">
                PostgreSQL 16.3 • asyncpg driver • TLS Active
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-xs">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground font-medium">Connection Pool</span>
                  <span className="font-mono text-foreground font-semibold">
                    14/100 active <span className="text-muted-foreground font-normal">(14% utilization)</span>
                  </span>
                </div>
                <Progress value={14} className="h-2" />
                <div className="flex justify-between text-[11px] text-muted-foreground">
                  <span>14 active</span>
                  <span>86 idle / reserved</span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-border">
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/60">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Cache Hit</div>
                  <div className="text-base font-bold font-mono text-emerald-400 mt-0.5">99.4%</div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/60">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Latency p50</div>
                  <div className="text-base font-bold font-mono text-foreground mt-0.5">1.2ms</div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/60">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Latency p95</div>
                  <div className="text-base font-bold font-mono text-foreground mt-0.5">4.8ms</div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/60">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Latency p99</div>
                  <div className="text-base font-bold font-mono text-amber-400 mt-0.5">11.4ms</div>
                </div>
              </div>

              <div className="flex items-center justify-between text-[11px] pt-1 text-muted-foreground border-t border-border">
                <span>Migration State:</span>
                <span className="font-mono text-foreground font-medium">Alembic 0006_head (synced)</span>
              </div>
            </CardContent>
          </Card>

          {/* Host Resource Indicators Card */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-primary" />
                  Host Resource Indicators
                </CardTitle>
                <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 text-xs font-semibold">
                  Host Healthy
                </Badge>
              </div>
              <CardDescription className="text-xs">
                GCP Compute Engine • e2-standard-2 (southamerica-west1-a)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-xs">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                    <Cpu className="h-3.5 w-3.5 text-primary" /> CPU Load
                  </span>
                  <span className="font-mono text-foreground font-semibold">1.8%</span>
                </div>
                <Progress value={1.8} className="h-2" />
                <div className="flex justify-between text-[11px] text-muted-foreground">
                  <span>Load Avg: 0.42, 0.38, 0.35</span>
                  <span>2 vCPUs allocated</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                    <Layers className="h-3.5 w-3.5 text-primary" /> Memory Usage
                  </span>
                  <span className="font-mono text-foreground font-semibold">1.4 GB / 4.0 GB (35%)</span>
                </div>
                <Progress value={35} className="h-2" />
                <div className="flex justify-between text-[11px] text-muted-foreground">
                  <span>2.6 GB available</span>
                  <span>4.0 GB total ECC</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                    <HardDrive className="h-3.5 w-3.5 text-primary" /> Disk Storage
                  </span>
                  <span className="font-mono text-foreground font-semibold">18.2 GB / 50.0 GB (36%)</span>
                </div>
                <Progress value={36} className="h-2" />
                <div className="flex justify-between text-[11px] text-muted-foreground">
                  <span>31.8 GB free (ext4)</span>
                  <span>NVMe SSD Persistent Disk</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Docker Containers Card & Table */}
        <Card className="border-border">
          <CardHeader className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Server className="h-4 w-4 text-primary" />
                  Docker Containers
                </CardTitle>
                <CardDescription className="text-xs">
                  Service containers running in isolated Docker network
                </CardDescription>
              </div>
              <Badge variant="outline" className="w-fit text-xs font-mono font-medium text-emerald-400 border-emerald-500/30 bg-emerald-500/10">
                {infrastructure ? `${infrastructure.filter(c => c.state === "running").length} / ${infrastructure.length} Running` : "Loading..."}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Container</TableHead>
                  <TableHead>Service Role</TableHead>
                  <TableHead>Image</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uptime</TableHead>
                  <TableHead>CPU</TableHead>
                  <TableHead className="text-right">Memory</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(infrastructure || []).map((container) => (
                  <TableRow key={container.name}>
                    <TableCell className="font-mono text-xs font-semibold text-foreground">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-400" />
                        {container.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {container.role}
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {container.image}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-mono text-[10px]">
                        {container.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {container.uptime}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {container.cpu}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-foreground text-right font-medium">
                      {container.memory}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Security Governance Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Security Controls & Policies */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Security Governance
            </CardTitle>
            <CardDescription className="text-xs">
              Platform administration controls and invariants
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-xs">
            <div className="flex items-center justify-between py-1.5 border-b border-border">
              <span className="text-muted-foreground">Session Cookie</span>
              <Badge variant="outline" className="text-emerald-400 border-emerald-500/30">
                __Host- Prefix (RFC 6265bis)
              </Badge>
            </div>
            <div className="flex items-center justify-between py-1.5 border-b border-border">
              <span className="text-muted-foreground">Tenancy Scoping</span>
              <span className="font-mono text-foreground text-[11px]">X-Organization-Id</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-b border-border">
              <span className="text-muted-foreground">Impersonation Guard</span>
              <Badge variant="outline" className="text-amber-400 border-amber-500/30">
                Read-Only (403 Blocking)
              </Badge>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <span className="text-muted-foreground">Bundle Segregation</span>
              <span className="text-emerald-400 flex items-center gap-1 font-medium">
                <CheckCircle2 className="h-3 w-3" /> Zero Bleed
              </span>
            </div>

            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5 text-xs"
                onClick={() => setImpersonationOpen(true)}
              >
                <UserCheck className="h-3.5 w-3.5 text-primary" />
                Launch Diagnostic Impersonation
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Platform Audit Events */}
        <Card className="lg:col-span-2 border-border">
          <CardHeader className="flex flex-row items-center justify-between py-4">
            <div>
              <CardTitle className="text-base">Recent Platform Audit Events</CardTitle>
              <CardDescription className="text-xs">
                Real-time cross-tenant administrative actions
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 text-xs text-primary"
              onClick={() => navigate("/audit-logs")}
            >
              View Full Audit Trail
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {auditLoading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Loading audit trail...
              </div>
            ) : !auditData?.items?.length ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No platform audit events recorded yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Time</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditData.items.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {log.createdAt ? new Date(log.createdAt).toLocaleTimeString() : "—"}
                      </TableCell>
                      <TableCell className="font-medium text-xs">
                        {log.operatorEmail}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono text-[10px]">
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {log.resourceType || "system"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className={
                            log.statusCode >= 200 && log.statusCode < 300
                              ? "text-emerald-400 border-emerald-500/30"
                              : "text-destructive border-destructive/30"
                          }
                        >
                          {log.statusCode || 200}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <ImpersonationModal
        open={impersonationOpen}
        onOpenChange={setImpersonationOpen}
      />
    </PageContainer>
  );
}
